<script>
import { mapActions, mapState } from "vuex";
import i18n from "@/plugins/i18n";

import notificationsButton from './notificationsButton.vue';
import auditorNotificationsButton from './auditor/notificationsButton.vue';

const SustLogo = "/images/SustAppLogos/sustainfinity_256x256.png";
const CarbonLogo = "/images/SustAppLogos/Sustainfinity-Carbon-Logo.png";

export default {
  components: {
    notificationsButton,
    auditorNotificationsButton,
  },

  data() {
    return {
      activeNotifications: false,
      language: null,

      companyName: "",

      profileDropdownOpen: false,
      languagesDropdownOpen: false,

      languages: [
        { val: "tr", text: i18n.t('languages.tr') },
        { val: "en", text: i18n.t('languages.en') }
      ],

      appsOpen: false,

      myCompanies: [],
      companySearchWord: '',
    };
  },

  computed: {
    ...mapState('app', [
      'user',
      'adminPanel',
      'companyLicence',
    ]),
    
    ...mapState('company', [
      'logo',
    ]),

    isAuditor() {
      return this.companyLicence.plan_id === -1;
    },

    routeName() {
      const { meta } = this.$route;
      return this.$t(meta.key);
    },

    apps() {
      const base = [
        { val: "sust", type: "app", logo: SustLogo, text: "Sustainfinity", icon: null },
        { val: "carbon", type: "app", logo: CarbonLogo, text: "Carbon Module", icon: null },
        { val: "kpi", type: "module", logo: null, text: i18n.t('dropdown.kpi'), icon: "fa-solid fa-chart-line" },
        { val: "supplier", type: "module", logo: null, text: i18n.t('dropdown.supplier'), icon: "fi fi-rr-supplier-alt" },
        { val: "admin", type: "module", logo: null, text: i18n.t('dropdown.admin'), icon: "fi fi-rr-user-gear" },
      ];

      if (this.$user.role_id <= 4) {
        return [...base, { val: "my_team", type: "module", logo: null, text: i18n.t('route_names.admin_panel.children.team_members'), icon: "fi fi-rr-users" }];
      } else {
        return base;
      }
    },

    profileDropdownItems() {
      if (this.myCompanies.length > 1) {
        return [
          { val: "companies", text: i18n.t('dropdown.companies') },
          { val: "lang", text: i18n.t('dropdown.languages') },
          { val: "logout", text: i18n.t('dropdown.logout') }
        ];
      } else {
        return [
          { val: "admin", text: i18n.t('dropdown.admin') },
          { val: "lang", text: i18n.t('dropdown.languages') },
          { val: "logout", text: i18n.t('dropdown.logout') }
        ];
      }
    },

    shortFirstname() {
      if (this.$user.firstname.length <= 8) {
        return this.$user.firstname;
      } else {
        return this.$user.firstname.substring(0, 8) + ".";
      }
    },

    nameSpan() {
      if (this.$user.firstname.length + this.$user.surname.length < 12) {
        return this.shortFirstname + " " + this.$user.surname;
      } else {
        return this.shortFirstname + " " + this.$user.surname[0] + ".";
      }
    },

    filteredCompanies() {
      if (this.$user.role_id != 1 || this.myCompanies.length <= 1) return [];

      if (this.companySearchWord.length === 0) {
        return this.myCompanies;
      } else {
        return this.myCompanies.filter(x => this.$normalizeString(x.company_name).includes(this.$normalizeString(this.companySearchWord)));
      }
    },
  },

  updated() {
    this.sessionControl();
  },

  mounted() {
    this.getCompaniesIfAdmin();
    this.setCompanyInfo();

    if (!localStorage.getItem("currency")) {
      localStorage.setItem("currency", 2)
    }

    this.language = localStorage.getItem("lang");
    this.currency = localStorage.getItem("currency");
  },

  methods: {
    ...mapActions("app", [
      "destroyToken",
      "getUserCompanyInfo",
      "retrieveNewCompanyToken"
    ]),
    ...mapActions("company", [
      "getCompaniesAsAdmin",
    ]),

    getCompaniesIfAdmin() {
      if (this.$user && this.$user.role_id != 1) return;

      this.getCompaniesAsAdmin()
        .then(res => {
          this.myCompanies = res.data;
        })
    },

    setCompanyInfo() {
      this.getUserCompanyInfo()
        .then(res => {
          this.companyName = res.data.company_name;
          localStorage.setItem('sector_id', res.data.sector_id);
        })
        .catch(err => {
          console.log(err)
        })
    },

    async _retrieveNewToken(company) {
      const params = {
        company_id: company.company_id,
        company_name: company.company_name,
        role_id: this.$user.role_id
      }

      await this.retrieveNewCompanyToken(params)
        .then(async (response) => {
          await this.$notify("success", response.data.messageName);
        })
        .catch(async ({ response }) => {
          await this.$notify("error", response.data.messageName)
        })
    },

    capitalizeFirstLetter(string) {
      return string.toUpperCase();
    },

    logOut() {
      this.destroyToken({ user_id: this.$user.user_id })
        .then(res => {
          this.$notify('success', res.data.messageName);
          localStorage.clear();
          sessionStorage.clear();
        })
        .finally(() => {
          this.$router.push("/login");
        })
    },

    sessionControl() {
      let logoutTime;
      if (localStorage.logoutTime) {
        logoutTime = localStorage.logoutTime;
        let currentDate = new Date().getTime();
        if (currentDate >= logoutTime) {
          setTimeout(() => {
            this.logOut();
          }, 150);
        }
      } else {
        setTimeout(() => {
          this.logOut();
        }, 1500);
      }
    },
    getDeviceLang() {
      const userLang = String(navigator.language).substring(0, 2)[3] || String(navigator.userLanguage).substring(0, 2)[3];
      return userLang;
    },
    async selectCompany(company) {
      let done = false;

      const setDone = async (_done) => {
        done = _done;
      }

      const reload = () => {
        if (done) {
          window.location.reload();
        } else {
          setTimeout(() => {
            reload();
          }, 250);
        }
      }

      try {
        await setDone(false);
        await this._retrieveNewToken(company);
        await setDone(true);
      } catch (error) {
        console.log(error.message);
      } finally {
        reload();
      }
    },

    // EVENT HANDLERS //

    HandleProfileDropdown() {
      if (!this.profileDropdownOpen && this.languagesDropdownOpen) {
        this.ResetDropdown();
      } else {
        this.profileDropdownOpen = !this.profileDropdownOpen;
      }
    },

    OpenLanguagesDropdown() {
      this.languagesDropdownOpen = true;
      this.profileDropdownOpen = false;
    },

    OpenCompaniesPopup() {
      this.$refs.companies_if_admin.openPopup();
    },

    ResetDropdown() {
      setTimeout(() => {
        this.profileDropdownOpen = false;
        this.languagesDropdownOpen = false;
      }, 0);
    },

    ClickDropdownItem(item) {
      if (item.val === "logout") {
        this.logOut();
      } else if (item.val === "admin") {
        this.openAdminPanel();
      } else if (item.val === "lang") {
        this.OpenLanguagesDropdown();
      } else if (item.val === "companies") {
        this.OpenCompaniesPopup();
      } else {
        return;
      }
    },

    ClickCloseLanguage() {
      this.languagesDropdownOpen = false;
      this.profileDropdownOpen = true;
    },

    ChangeLanguage(item) {
      localStorage.setItem("lang", item);
      window.location.reload()
    },

    openAdminPanel() {
      this.$router.push({ path: '/admin-panel' });
    },

    OpenAppsController() {
      this.appsOpen = !this.appsOpen;
    },

    CloseApps() {
      this.appsOpen = false;
    },

    async ClickApp(item) {
      try {
        if (item.val === "sust") {
          this.$router.push({ path: '/dashboard' });
        } else if (item.val === "carbon") {
          window.open(process.env.VUE_APP_CARBON_DOMAIN);
        } else if (item.val === "kpi") {
          this.$router.push({ path: "/kpi-metrics" });
        } else if (item.val === "supplier") {
          this.$router.push({ path: "/supplier-metrics" });
        } else if (item.val === "admin") {
          this.$router.push({ path: "/admin-panel/user-profile" });
        } else if (item.val === "my_team") {
          this.$router.push({ path: '/admin-panel/team-members' });
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        this.CloseApps();
      }
    },
  },
};
</script>

<template>
  <nav class="top-navbar">
    <div class="sustainfinity-banner-logo scale-better-in-4k">
      <a href="/dashboard">
        <img src="/images/sustainfinity_256x256.png">
      </a>
    </div>

    <div class="navbar-route-name">
      <a class="navbar-brand" style="font-weight: bold; color: var(--sustainfinity-navy); text-transform: uppercase;">
        {{ routeName }}
      </a>
    </div>

    <div class="navbar-right-side-buttons">
      <notifications-button v-if="!isAuditor" />
      <auditor-notifications-button v-else />

      <div class="app-selection-container popover-arrow" @blur="CloseApps()" @keydown.esc="CloseApps()" tabindex="0">
        <i class="fi fi-ss-apps" @click="OpenAppsController()"></i>

        <Transition name="fade">
          <div class="apps-container" v-if="appsOpen">
            <div class="app" v-for="(item, index) in apps" :key="index" @click="ClickApp(item)">
              <div class="app-inner" v-if="item.type == 'app'">
                <img :src="item.logo" :alt="item.text">
              </div>
              <div class="app-inner" v-if="item.type == 'module'">
                <i :class="item.icon"></i>
                <span>{{ item.text }}</span>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <div class="profile-dropdown-container navbar-drop-button" @blur="ResetDropdown()" @keydown.esc="ResetDropdown()"
        tabindex="1">
        <!-- ditched v-clickoutside custom directive and made element focusable therefore blurrable (unfocusable) -->
        <div class="profile-dropdown-button" @click="HandleProfileDropdown()">
          <div class="name-container">
            <span>{{ nameSpan }}</span>
          </div>
          <div v-if="logo == null" class="logo-container no-logo">
            <i class="fa-regular fa-building"></i>
          </div>
          <div v-else class="logo-container" style="background-color: whitesmoke;">
            <img :src="logo">
          </div>
        </div>

        <Transition name="fade">
          <div v-if="profileDropdownOpen" class="profile-dropdown-list">
            <div class="company-name-span">
              {{ companyName }}
            </div>
            <div v-for="(item, index) in profileDropdownItems" :key="index" class="profile-dropdown-item">
              <span @click="ClickDropdownItem(item)" :id="item.val === 'logout' ? 'logout-button' : null">
                {{ item.text }}
              </span>
            </div>
          </div>

          <div v-if="languagesDropdownOpen" class="profile-dropdown-list">
            <div class="company-name-span">
              <i class="fa-solid fa-circle-arrow-left" @click="ClickCloseLanguage()"></i>
              {{ $t('dropdown.languages') }}
            </div>
            <div v-for="(item, index) in languages" :key="index" class="profile-dropdown-item">
              <span @click="ChangeLanguage(item.val)">{{ item.text }}</span>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <sust-popup
      v-if="$user.role_id === 1"
      ref="companies_if_admin"
      size="md"
      :footer="false"
      :header="false"
    >
      <input type="search" class="sust-input-field" 
        style="width: 80%; padding: .6em .4em; border-radius: .5em;"
        :placeholder="$t('misc.search_for_company')" 
        v-model="companySearchWord"
      >

      <div v-for="(company, index) in filteredCompanies" :key="index"
        class="flex-all-centered-row" style="border-bottom: thin solid lightgray; padding: .5em 0; font-weight: bold;"
      >
        <div style="width: 10%;">
          {{ index + 1 }}
        </div>
        <div style="width: 30%;">
          {{ company.company_name }}
        </div>
        <button class="sust-btn sust-btn-green" style="width: 10%;"
          @click="selectCompany(company)"
        >
          {{ $t('misc.select') }}
        </button>
      </div>
    </sust-popup>
  </nav>
</template>

<style lang="scss">
.fade-active {
  transition: opacity .66s ease;
}

.fade-from,
.fade-to {
  opacity: 1;
}

.top-navbar {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.sustainfinity-banner-logo {
  flex: 1;
  min-width: 15%;
  max-width: 15%;
  max-height: 50px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  >a>img {
    height: 150px;
    display: block;
  }
}

.navbar-route-name {
  flex: 6;
  padding-left: 2.5em;
}

.navbar-right-side-buttons {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  flex: 1;

  >div {
    height: fit-content !important;

    >i {
      line-height: 0 !important;
    }
  }
}

.navbar-drop-menu {
  position: absolute;
  z-index: 1 !important;

  width: inherit;
  max-width: 135px;
  top: 3rem;

  max-height: 20em;
  overflow-y: auto;
  overflow-x: hidden;

  background-color: #ffffff;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  border: thin solid black;
  filter: drop-shadow(0 0 .4em gray);
}

.navbar-drop-menu::-webkit-scrollbar {
  display: none;
}

.drop-menu-content {
  color: var(--sustainfinity-navy) !important;
}

.drop-menu-item {
  padding-top: .2rem;
  cursor: pointer;
  font-size: 13px;
}

.drop-menu-item:last-of-type {
  padding-bottom: .33rem;
}

.drop-menu-item:hover {
  font-weight: bold;
}

.drop-menu-back-button:hover {
  cursor: pointer;
}

.no-border {
  border: 0;
  box-shadow: none;
}

.name-span {
  display: flex;
  text-align: left;
  align-content: center;
}

.custom-rounded {
  border-top-left-radius: 30px;
  border-bottom-left-radius: 30px;
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
}

.navbar-image-container {
  max-height: 40px;
  max-width: 40px;
  border-radius: 20px;
  background-color: white;
  overflow: hidden;
}

.navbar-menu-logo-image {
  width: calc(100% - 4px);
  height: 100%;
  object-fit: scale-down;
  object-position: center;
}

.profile-dropdown-container {
  position: relative;
  height: 40px;
  width: 125px;
  margin-right: 2em;

  display: flex;
  flex-direction: column;


  .profile-dropdown-button {
    z-index: 111;

    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-content: center;
    align-items: center;

    width: 100%;

    border-radius: 5em;

    background-color: var(--sustainfinity-navy);
    border: thin solid var(--sustainfinity-navy);

    height: 40px !important;

    cursor: pointer;

    &:hover {
      background-color: var(--sustainfinity-navy--hover);
    }

    >.name-container {
      color: whitesmoke;
      padding: .33em 0em .33em 1em;

      >span {
        font-size: 12px;
        white-space: nowrap;
      }
    }

    >.logo-container {
      margin-left: 10px;

      width: 100px !important;
      height: 40px;

      border-radius: 5em;
      overflow: hidden;

      display: flex;
      flex-direction: row;
      justify-content: center;
      align-content: center;
      align-items: center;

      >img {
        width: calc(100% - 4px);
        height: 100%;
        object-fit: scale-down;
        object-position: center;
      }
    }

    >.no-logo {
      color: white;
    }
  }

  &:focus {
    border: 0;
    outline: 0;
  }

  &:active {
    border: 0;
    outline: 0;
  }
}

.profile-dropdown-list {
  z-index: 110;

  position: absolute;

  top: 1.25rem;
  left: 50.5%;
  transform: translateX(-50.5%);

  padding-top: 1.5em;
  padding-bottom: .5em;

  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;

  background-color: whitesmoke;

  border-bottom-left-radius: 1em;
  border-bottom-right-radius: 1em;
  border: thin solid var(--sustainfinity-navy);
  box-shadow: 0 0 .33em .1em gray;

  &::-webkit-scrollbar {
    display: none !important;
  }

  &>.company-name-span {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-content: center;
    align-items: center;

    font-style: italic;
    font-size: .9em;
    font-weight: bold;
    color: var(--sustainfinity-navy);
    text-align: left;
    margin-left: 1em;
    margin-right: 2em;

    border-bottom: .2em solid var(--sustainfinity-navy);

    white-space: nowrap;

    >i {
      padding-top: .2em;
      padding-right: .2em;
      cursor: pointer;

      &:hover {
        filter: brightness(150%);
      }
    }
  }

  &>.profile-dropdown-item {
    width: 100%;

    margin-left: 1em;

    display: flex;
    justify-content: left;
    align-content: center;

    margin-top: .15em !important;

    &>span {
      width: 100%;
      color: var(--sustainfinity-navy);
      text-align: left;
      font-size: .8em;

      cursor: pointer;

      &:hover {
        font-weight: bold;
      }
    }
  }

  transition: opacity 100ms;
}

.company-dropdown-item {
  border-bottom: 0.001em solid lightgray !important;
  padding: .33em 0 .33em 0 !important;

  &:hover {
    background-color: rgb(230, 230, 230) !important;
  }
}

.app-selection-container {
  margin-right: 1em;

  &:active {
    border: 0;
    outline: 0;
  }

  >i {
    padding-top: .3em;
    font-size: 2em;
    color: var(--sustainfinity-navy);
    animation: glow 3s infinite ease-in-out;

    cursor: pointer;

    &:hover {
      color: black !important;
    }
  }

  z-index: 110;
  position: relative;

  >.apps-container {
    position: absolute;
    transform: translateY(100%);
    bottom: 0px;
    right: 0;

    width: 200px;

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-content: center;
    flex-wrap: wrap;

    background-color: whitesmoke;
    border: thin solid var(--sustainfinity-navy);
    border-radius: 1em;

    padding: .5em;

    >.app {
      width: 50%;
      min-height: 70px;
      max-height: 70px;
      border-radius: 7px;

      display: flex;
      flex-direction: row;
      justify-content: center;
      align-content: center;
      align-items: center;

      cursor: pointer;

      &:hover {
        background-color: lightgray;
      }

      >.app-inner {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-content: center;
        align-items: center;
        text-align: center;
        color: black;

        >img {
          width: 100%;
        }

        >span {
          font-weight: bold;
          font-style: italic;
          font-size: .9em;
        }
      }
    }
  }
}

@keyframes glow {
  0% {
    color: var(--sustainfinity-navy);
  }

  50% {
    color: #0870a4;
  }

  100% {
    color: var(--sustainfinity-navy);
  }
}

@media (min-width: 3500px) {
  .scale-better-in-4k {
    width: 6% !important;
    margin-left: .01% !important;
  }
}
</style>
